import {
  buildOrderLiteral,
  buildPagingLiteral,
  buildWhereLiteral,
  OrderBy,
} from '@/lib/graphql-query-builder';

/**
 * The Blocks OS gateway names query operations `get<Collection>` (`getDepartments`), not the bare
 * collection name. The alias keeps the response key `Departments` so services and hooks read
 * `result.Departments` unchanged. Confirm exact names with `blocks data schema get <id>`.
 *
 * A to Z by title by default, like the old department list's `{ Title: 1 }` sort.
 */
export const DEFAULT_DEPARTMENTS_ORDER: OrderBy[] = [{ field: 'Title', direction: 'ASC' }];

export const buildGetDepartmentsQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order: OrderBy[] = DEFAULT_DEPARTMENTS_ORDER
) => `
  query {
    Departments: getDepartments(
      where: ${buildWhereLiteral(where)}
      order: ${buildOrderLiteral(order)}
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      hasNextPage
      hasPreviousPage
      totalCount
      totalPages
      pageSize
      pageNo
      items {
        ItemId
        CreatedDate
        CreatedBy
        LastUpdatedDate
        LastUpdatedBy
        Language
        OrganizationId
        Tags
        Title
        Key
        Description
      }
    }
  }
`;

/** Lightweight lookup for pickers/filters elsewhere in the app (attendance, timesheet, ...) —
 * ItemId + Title only, unlike buildGetDepartmentsQuery's full admin field set. */
export const buildGetDepartmentsLookupQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number
) => `
  query {
    Departments: getDepartments(
      where: ${buildWhereLiteral(where)}
      order: []
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      totalCount
      items {
        ItemId
        Title
        Key
        OrganizationId
      }
    }
  }
`;

/** People (`UserProfileDetail`) still assigned to a department — only the count is read. */
export const buildCountDepartmentEmployeesQuery = (departmentId: string) => `
  query {
    Employees: getUserProfileDetails(
      where: ${buildWhereLiteral({ DepartmentId: departmentId })}
      order: []
      paging: ${buildPagingLiteral(1, 1)}
    ) {
      totalCount
    }
  }
`;

/** Active (not resigned) people in a department — Department has no stored count, so the
 * table's "Active employee" column is derived from `UserProfileDetail`. */
export const buildCountActiveDepartmentEmployeesQuery = (departmentId: string) => `
  query {
    Employees: getUserProfileDetails(
      where: ${buildWhereLiteral({ DepartmentId: departmentId, IsResigned: { $ne: true } })}
      order: []
      paging: ${buildPagingLiteral(1, 1)}
    ) {
      totalCount
    }
  }
`;
